/**
 * useConversationListSocket — listener global que mantém a lista de conversas
 * (infinite query `['conversations', 'infinite', ...]` + query simples
 * `['conversations', ...]`) sincronizada com eventos socket.
 *
 * Escuta `CONVERSATION_MESSAGE` (broadcast — sempre emitido, independente de
 * assignee/privacy). Para conversa já no cache: atualiza `lastMessage`,
 * `lastMessageAt` e `messageCount` in-place via `setQueriesData` — exceto
 * quando `message.isInternal` é `true`, caso em que prévia, relógio e
 * contador ficam intocados (nota interna / "Atribuição alterada" não é fala
 * da conversa nem novidade a ser lida).
 * Para conversa nova (não encontrada no cache): invalida `['conversations']`
 * com `exact: false` forçando refetch.
 *
 * Deve ser montado UMA ÚNICA VEZ no root (via `GlobalSocketListeners`) pra
 * garantir que a lista reaja mesmo quando o usuário não está na rota de chat.
 * `useChatSocket` (local no chat) mantém só a lógica de `['messages', ...]`.
 */

import { useQueryClient } from '@tanstack/react-query';
import { SOCKET_EVENTS as Events } from '@troia-v3/types';
import type { ConversationMessageResponse } from '@troia-v3/types';
import { useEffect } from 'react';

import { useSocket } from '@/contexts/SocketContext';

export const useConversationListSocket = (): void => {
  const queryClient = useQueryClient();
  const { socket } = useSocket();

  useEffect(() => {
    if (!socket) return;

    const handler = (message: ConversationMessageResponse): void => {
      if (!message.id || !message.conversationId) return;

      // Mensagem INTERNA (nota interna, registro de transferência
      // "Atribuição alterada") não é fala da conversa: não pode virar prévia do
      // card nem resetar o relógio/ordenação por inatividade. O backend já
      // ignora essas mensagens ao gravar `lastMessage`/`lastMessageAt`
      // (guard em `ConversationMessagesService.createMessage`) — aqui mantemos
      // a paridade no cache ao vivo, senão a lista mostraria o texto interno
      // até o próximo refetch. As demais reações (contador, invalidação de
      // conversa nova) seguem valendo.
      const isInternalMessage = message.isInternal === true;

      let conversationFoundInCache = false;

      queryClient.setQueriesData(
        { queryKey: ['conversations'], exact: false },
        (oldData: any) => {
          if (!oldData?.pages) return oldData;

          const newPages = oldData.pages.map((page: any) => {
            const newItems = page.items.map((conv: any) => {
              if (conv.id === message.conversationId) {
                conversationFoundInCache = true;
                return {
                  ...conv,
                  ...(isInternalMessage ? {} : {
                    lastMessage: message.plainText || '',
                    lastMessageAt: message.sentAt || new Date().toISOString(),
                    // Mensagem interna também não infla o contador de
                    // não-lidas (paridade com o guard de
                    // `incrementUnreadCount` no backend) — nota interna e
                    // "Atribuição alterada" não são novidade visível na
                    // conversa para quem está lendo o card.
                    messageCount: (conv.messageCount || 0) + 1,
                  }),
                };
              }
              return conv;
            });

            return { ...page, items: newItems };
          });

          return conversationFoundInCache
            ? { ...oldData, pages: newPages }
            : oldData;
        }
      );

      if (!conversationFoundInCache) {
        queryClient.invalidateQueries({ queryKey: ['conversations'], exact: false });
      }
    };

    socket.on(Events.CONVERSATION_MESSAGE, handler);
    return () => {
      socket.off(Events.CONVERSATION_MESSAGE, handler);
    };
  }, [socket, queryClient]);
};